import Highcharts from 'highcharts';
import colors from 'tailwindcss/colors';
import moment from 'moment';
import MarketRegionState from '@app/model/market_area/MarketAreaState';
import { marketRegionStateClusterColors } from '@app/model/market_area/MarketRegionStateCluster';
import { MarketPaceDay } from '@app/model/market_insight/MarketPace';
import { demandHoverHtml, paceHoverHtml } from './MarketPaceText';
import { PACE_CELL_COLOR, PACE_CLASS_OF } from './MarketPaceStyles';

// The Market Demand Chart, refreshed (grill decision 7):
//   R1 the selected date has a blue band over the bar and a blue ring on its pace cell;
//   R3 a hover shows the date and the demand (bar) or the pace verdict and why (cell);
//   R4 a label on every Monday ("Mon 5 Oct"), except on the first and the last date of the chart,
//      where the label does not fit; a gray band on Saturday and Sunday.
// The pace strip is a second pane of the same chart (decision 6): one cell under each bar.

type Input = {
	states: MarketRegionState[],
	// null: the hotel has no pace strip (the setting is off, or the pace did not load)
	pace: Map<string, MarketPaceDay> | null,
	selectedIndex: number,
	phone: boolean,
	onSelect: (index: number) => void,
};

// A stay date without a pace result in the run shows as "no comparison" (a Booking.com scan is absent).
export const paceDayOf = (pace: Map<string, MarketPaceDay>, statDate: string): MarketPaceDay =>
	pace.get(statDate) ?? {
		statDate, verdict: 'NO_COMPARISON', reason: 'NO_DATA', ownStep: null, marketStep: null, leadInterval: 'DAYS_0_15', evidence: null,
	};

export const buildDemandChartOptions = ({ states, pace, selectedIndex, phone, onSelect }: Input): Highcharts.Options => {
	const showPace = pace !== null;
	const bands: Highcharts.XAxisPlotBandsOptions[] = [];
	states.forEach((state, i) => {
		const day = moment(state.statDate).isoWeekday();
		if (day === 6 || day === 7) bands.push({ from: i - 0.5, to: i + 0.5, color: colors.gray[100], zIndex: 0 });
	});
	if (selectedIndex >= 0) bands.push({ from: selectedIndex - 0.5, to: selectedIndex + 0.5, color: colors.blue[100], zIndex: 1 });

	const click = { click: function(this: Highcharts.Point) { onSelect(this.index); } };

	const demandSeries: Highcharts.SeriesColumnOptions = {
		type: 'column',
		name: 'Demand',
		yAxis: 0,
		borderRadius: 3,
		pointPadding: phone ? 0.16 : 0.14,
		data: states.map(state => ({ y: Number(state.demand), color: marketRegionStateClusterColors[state.cluster] })),
	};

	const paceSeries: Highcharts.SeriesColumnOptions = {
		type: 'column',
		name: 'Pace vs market',
		yAxis: 1,
		borderRadius: 2,
		pointPadding: 0.06,
		data: states.map((state, i) => {
			const cls = pace === null ? 'NONE' : PACE_CLASS_OF[paceDayOf(pace, state.statDate).verdict];
			const selected = i === selectedIndex;
			return {
				y: 1,
				color: PACE_CELL_COLOR[cls],
				borderColor: selected ? colors.blue[600] : cls === 'NONE' ? colors.gray[300] : undefined,
				borderWidth: selected ? 2 : cls === 'NONE' ? 1 : 0,
			};
		}),
	};

	return {
		chart: { height: phone ? 250 : 290, spacing: [10, 4, 6, 4], marginBottom: 32, style: { fontFamily: 'inherit' }, animation: false },
		title: { text: '' },
		credits: { enabled: false },
		legend: { enabled: false },
		xAxis: {
			categories: states.map(state => state.statDate),
			tickLength: 0,
			lineColor: colors.gray[200],
			plotBands: bands,
			labels: {
				step: 1,
				rotation: 0,
				style: { fontSize: '11px', color: colors.gray[500], textOverflow: 'none', whiteSpace: 'nowrap' },
				formatter: function() {
					const statDate = String(this.value);
					if (moment(statDate).isoWeekday() !== 1) return '';
					// A label on the first or the last date runs over the edge of the chart, and Highcharts cuts it.
					if (this.pos === 0 || this.pos === states.length - 1) return '';
					const label = moment(statDate).format('ddd D MMM');
					return states[selectedIndex]?.statDate === statDate ? `<span style="font-weight:600">${label}</span>` : label;
				},
			},
		},
		yAxis: [
			{ top: '0%', height: showPace ? '88%' : '100%', min: 0, max: 100, gridLineWidth: 0, labels: { enabled: false }, title: { text: undefined } },
			{ top: '93%', height: '7%', offset: 0, min: 0, max: 1, gridLineWidth: 0, labels: { enabled: false }, title: { text: undefined }, visible: showPace },
		],
		plotOptions: {
			series: { animation: false, cursor: 'pointer', point: { events: click }, states: { inactive: { enabled: false } } },
			column: { grouping: false, borderWidth: 0, groupPadding: 0 },
		},
		tooltip: {
			useHTML: true,
			outside: true,
			shared: false,
			backgroundColor: colors.white,
			borderColor: colors.gray[200],
			borderRadius: 8,
			padding: 10,
			formatter: function() {
				const state = states[this.index];
				if (state === undefined) return false;
				return this.series.index === 1 && pace !== null ? paceHoverHtml(paceDayOf(pace, state.statDate)) : demandHoverHtml(state);
			},
		},
		series: showPace ? [demandSeries, paceSeries] : [demandSeries],
	};
};
